/**
 * The flight workspace: the map at the centre, the flight above it, the
 * inspector beside it — and, underneath, exactly the machinery the old editor
 * had. Loading, the aircraft feed, the openAIP enrichment, autosave to
 * localStorage and to the API with optimistic versioning, the conflict path and
 * the PDF are ported unchanged; what moved is where the pilot meets them.
 *
 * Signed out (or at `/`) it works as the single-file predecessor did: everything
 * lives in this browser and the PDF is produced here.
 *
 * Provider order matters and is the reason they all sit in this one file:
 *   · `PlanProvider` — the document every pane reads;
 *   · `FreshnessProvider` — the four sources publish "how old is this" here;
 *   · `BannersProvider` — the panes publish what the pilot must decide about;
 *   · `MapBridgeProvider` — the canvas publishes fit/flyTo/centre/highlight, so
 *     the Strecke pane can sit in the inspector instead of on the map;
 *   · `WindsProvider` / `TerrainProvider` / `WxProvider` — the three unattended
 *     sources. They wrap the WHOLE workspace, not the pane that shows them: the
 *     wind under the route is a fact about the plan, not about a tab, and a
 *     pilot who never opens the Wetter tab still gets the ATIS rows.
 */
import {
  useCallback, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as api from '../api';
import { type AircraftFeed } from '../aircraft';
import { ReadOnlyProvider } from '../components/Field';
import { FreshnessProvider, usePublishFreshness } from '../freshness';
import { enrichPlan, geoSignature } from '../geo';
import { useLang, useT } from '../i18n';
import { RouteMap } from '../map';
import { MapBridgeProvider } from '../map/mapBridge';
import { useNarrow } from '../media';
import { PlanProvider, usePlanRev, usePlanStore } from '../planContext';
import { downloadPdf, pdfFilename } from '../pdf';
import { useSession } from '../session';
import { LOCAL_KEY, PlanStore, clearLocal, planKey, readLocal, saveLocal } from '../store';
import { TerrainProvider } from '../terrainContext';
import { useAutoLoad } from '../useAutoLoad';
import { WindsProvider } from '../windsContext';
import { WxProvider } from '../wxContext';
import { AppShell } from './AppShell';
import { Banners, useFlightBanners } from './Banners';
import { BannersProvider, type Banner } from './severity';
import { Inspector } from './Inspector';
import { MoreSheet, type MoreEntry } from './MoreSheet';
import { PaneSheet } from './PaneSheet';
import { SummaryStrip } from './SummaryStrip';
import { TabBar, type TabBarItem } from './TabBar';
import { IconBack, IconExport, IconForm, IconMap, IconMore, IconNavLog, IconWeather } from './icons';
import { TABS, type TabKey } from './tabs';

type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error' | 'conflict' | 'local';

const LS_INSPECTOR = 'aether.inspector.collapsed';

const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(LS_INSPECTOR) === '1';
  } catch {
    return false;
  }
};

/** The aircraft record as the PlanStore's feed wants it (see `aircraft.ts`). */
const feedOf = (a: api.Aircraft): AircraftFeed => ({
  id: a.id,
  registration: a.registration,
  typeCode: a.type_code,
  profile: a.profile,
});

/** Plan metadata is not part of the document, so renaming must still mark it dirty. */
const metaKey = (title: string, isTemplate: boolean, aircraftId: string | null): string =>
  title + ' ' + String(isTemplate) + ' ' + (aircraftId ?? '');

export interface WorkspaceProps {
  planId?: string | undefined;
  tab: TabKey;
  onTab(next: TabKey): void;
}

export default function FlightWorkspace({ planId, tab, onTab }: WorkspaceProps) {
  const store = useMemo(() => new PlanStore(), [planId]);
  return (
    <PlanProvider store={store}>
      <FreshnessProvider>
        <BannersProvider>
          <MapBridgeProvider>
            <WorkspaceInner planId={planId} tab={tab} onTab={onTab} />
          </MapBridgeProvider>
        </BannersProvider>
      </FreshnessProvider>
    </PlanProvider>
  );
}

function WorkspaceInner({ planId, tab, onTab }: WorkspaceProps) {
  const store = usePlanStore();
  const rev = usePlanRev();
  const session = useSession();
  const t = useT();
  const { lang } = useLang();
  const narrow = useNarrow();
  const navigate = useNavigate();

  const [loaded, setLoaded] = useState(false);
  /* errors are kept as thrown and translated at render, so switching language retranslates */
  const [loadError, setLoadError] = useState<unknown>(null);
  const [title, setTitle] = useState('');
  const [isTemplate, setIsTemplate] = useState(false);
  const [aircraftId, setAircraftId] = useState<string | null>(null);
  const [aircraft, setAircraft] = useState<api.Aircraft[]>([]);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState<unknown>(null);
  const [conflict, setConflict] = useState<api.Plan | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [more, setMore] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [autocalc, setAutocalc] = useState(true);
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);

  const versionRef = useRef(1);
  const savedRevRef = useRef(-1);
  /* plan metadata lives outside the document, so it needs its own dirty marker */
  const savedMetaRef = useRef('');
  const fileRef = useRef<HTMLInputElement>(null);

  const storageKey = planId ? planKey(planId) : LOCAL_KEY;

  /* ---- load ---- */
  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setLoadError(null);
    if (!planId) {
      const local = readLocal(LOCAL_KEY);
      if (local) store.loadDoc(local.doc);
      else store.recompute();
      savedRevRef.current = store.docRev;
      setSaveState('local');
      setLoaded(true);
      return;
    }
    void (async () => {
      try {
        const p = await api.getPlan(planId);
        if (cancelled) return;
        store.loadDoc({ values: p.values, manual: p.manual, route: p.route, meta: p.meta });
        versionRef.current = p.version;
        setTitle(p.title);
        setIsTemplate(p.is_template);
        setAircraftId(p.aircraft_id);
        savedRevRef.current = store.docRev;
        savedMetaRef.current = metaKey(p.title, p.is_template, p.aircraft_id);
        setSaveState('saved');
        setLoaded(true);
      } catch (e) {
        if (cancelled) return;
        // offline or logged out: fall back to whatever this browser still has
        const local = readLocal(planKey(planId));
        if (local) {
          store.loadDoc(local.doc);
          versionRef.current = local.version ?? 1;
          setTitle(local.title ?? '');
          savedRevRef.current = store.docRev;
          setSaveState('error');
          setSaveError(e);
          setLoaded(true);
        } else {
          setLoadError(e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [planId, store]);

  /* ---- aircraft profiles ---- */
  useEffect(() => {
    if (!session.user) {
      setAircraft([]);
      return;
    }
    let cancelled = false;
    void api
      .listAircraft()
      .then(list => {
        if (!cancelled) setAircraft(list);
      })
      .catch(() => {
        /* the picker is a convenience, not a requirement */
      });
    return () => {
      cancelled = true;
    };
  }, [session.user]);

  /* ---- the aircraft feed ----
     Whatever gives the plan its aircraft ends up here, and the store decides
     which cells the profile may fill. Waiting for `loaded` matters: the tags
     arrive with the document, and feeding before them would treat every stored
     value as the pilot's. */
  const planAircraft = useMemo(
    () => aircraft.find(a => a.id === aircraftId) ?? null,
    [aircraft, aircraftId],
  );

  useEffect(() => {
    if (!loaded) return;
    store.setAircraft(planAircraft ? feedOf(planAircraft) : null);
  }, [loaded, planAircraft, store]);

  /* ---- the openAIP feed (source 'geo') ----
     Everything the route already knows about the places it touches. It runs here
     rather than in the map because it is a property of the plan, not of the
     canvas — a pilot who never opens the Strecke tab still gets the cells. */
  const geoKey = useMemo(() => geoSignature(store, lang), [store, lang, rev]);

  const geo = useAutoLoad({
    signature: loaded ? geoKey : '',
    debounceMs: 300,
    run: ({ reason }) => enrichPlan(store, t, { force: reason === 'refresh' }),
  });

  usePublishFreshness(
    'geo',
    useMemo(
      () => ({ busy: geo.busy, error: geo.error, at: geo.at, refresh: geo.refresh }),
      [geo.busy, geo.error, geo.at, geo.refresh],
    ),
  );

  /* ---- autosave: localStorage always ---- */
  useEffect(() => {
    if (!loaded) return;
    const id = setTimeout(() => {
      saveLocal(storageKey, store.getDoc(), { version: versionRef.current, title });
    }, 250);
    return () => clearTimeout(id);
  }, [rev, loaded, storageKey, store, title]);

  useEffect(() => {
    const flush = () => saveLocal(storageKey, store.getDoc(), { version: versionRef.current, title });
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, [storageKey, store, title]);

  /* ---- autosave: the API when signed in ---- */
  const push = useCallback(async () => {
    if (!planId) return;
    const doc = store.getDoc();
    const revAtSend = store.docRev;
    const metaSig = metaKey(title, isTemplate, aircraftId);
    setSaveState('saving');
    setSaveError(null);
    try {
      const res = await api.updatePlan(planId, {
        title,
        is_template: isTemplate,
        aircraft_id: aircraftId,
        values: doc.values,
        manual: doc.manual,
        // always explicit: omitting the key means "keep the server's route",
        // which would make deleting the last waypoint impossible
        route: doc.route ?? null,
        // the derived-tag sets travel with the document, for the same reason
        meta: doc.meta ?? null,
        version: versionRef.current,
      });
      if (res.ok) {
        versionRef.current = res.plan.version;
        savedRevRef.current = revAtSend;
        savedMetaRef.current = metaSig;
        setSaveState('saved');
      } else {
        setConflict(res.server);
        setSaveState('conflict');
      }
    } catch (e) {
      setSaveState('error');
      setSaveError(e);
    }
  }, [planId, store, title, isTemplate, aircraftId]);

  useEffect(() => {
    if (!loaded || !planId || conflict) return;
    if (rev === savedRevRef.current && metaKey(title, isTemplate, aircraftId) === savedMetaRef.current) return;
    setSaveState(s => (s === 'saving' ? s : 'dirty'));
    const id = setTimeout(() => void push(), 900);
    return () => clearTimeout(id);
  }, [rev, loaded, planId, conflict, push, title, isTemplate, aircraftId]);

  /* ---- the connection: an autosave that cannot reach the server is not lost,
     but the pilot should know which of the two copies they are looking at ---- */
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  /* ---- keyboard: Alt+1…6 for the tabs, / for the route search ---- */
  useEffect(() => {
    const inField = (el: EventTarget | null): boolean => {
      const n = el as HTMLElement | null;
      return !!n && (n.tagName === 'INPUT' || n.tagName === 'TEXTAREA' || n.tagName === 'SELECT' || n.isContentEditable);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.key >= '1' && e.key <= '6') {
        const next = TABS[Number(e.key) - 1];
        if (next) {
          e.preventDefault();
          onTab(next.key);
        }
        return;
      }
      if (e.key === '/' && !e.altKey && !e.ctrlKey && !e.metaKey && !inField(e.target)) {
        e.preventDefault();
        onTab('route');
        setTimeout(() => document.querySelector<HTMLInputElement>('.mapsearch input')?.focus(), 60);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onTab]);

  /* ---- actions ---- */
  const onPdf = async () => {
    setPdfBusy(true);
    try {
      await downloadPdf(store.collect());
    } catch (e) {
      window.alert(t('err.pdf', { error: api.apiMessage(t, e) }));
    } finally {
      setPdfBusy(false);
    }
  };

  const onExport = () => {
    const values = store.collect();
    const blob = new Blob([JSON.stringify(values, null, 1)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = pdfFilename(values).replace(/\.pdf$/, '.json');
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 4000);
  };

  const onImportFile = async (file: File) => {
    try {
      const obj = JSON.parse(await file.text()) as unknown;
      if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) throw new Error(t('err.notAnObject'));
      const values: Record<string, string> = {};
      for (const [k, v] of Object.entries(obj as Record<string, unknown>)) values[k] = String(v ?? '');
      if (!store.isEmpty() && !window.confirm(t('ed.confirm.import'))) return;
      store.loadValues(values, true);
    } catch (e) {
      window.alert(t('err.file', { error: api.apiMessage(t, e) }));
    }
  };

  const onClear = () => {
    if (!window.confirm(t('ed.confirm.clear'))) return;
    store.loadValues({}, false);
    if (!planId) clearLocal(LOCAL_KEY);
  };

  const onShare = async () => {
    if (!planId) return;
    try {
      const s = await api.sharePlan(planId);
      setShareUrl(window.location.origin + s.url_path);
    } catch (e) {
      window.alert(t('err.share', { error: api.apiMessage(t, e) }));
    }
  };

  const takeServer = () => {
    if (!conflict) return;
    store.loadDoc({
      values: conflict.values,
      manual: conflict.manual,
      route: conflict.route,
      meta: conflict.meta,
    });
    versionRef.current = conflict.version;
    setTitle(conflict.title);
    setIsTemplate(conflict.is_template);
    setAircraftId(conflict.aircraft_id);
    savedRevRef.current = store.docRev;
    savedMetaRef.current = metaKey(conflict.title, conflict.is_template, conflict.aircraft_id);
    setConflict(null);
    setSaveState('saved');
  };

  const keepMine = () => {
    if (!conflict) return;
    versionRef.current = conflict.version;
    setConflict(null);
    savedRevRef.current = -1;
    void push();
  };

  /* ---- what the strip and the banners say ---- */
  const statusText: Record<SaveState, string> = {
    idle: '',
    local: t('ed.status.local'),
    dirty: t('ed.status.dirty'),
    saving: t('ed.status.saving'),
    saved: t('ed.status.saved'),
    error: t('ed.status.error'),
    conflict: t('ed.status.conflict'),
  };

  const extraBanners = useMemo<Banner[]>(() => {
    const list: Banner[] = [];
    if (conflict) list.push({ code: 'sync-conflict', severity: 'caution', key: 'ed.conflict.text' });
    if (saveState === 'error' && saveError) {
      list.push({
        code: 'save-failed',
        severity: 'caution',
        key: 'ed.saveerr',
        params: { error: api.apiMessage(t, saveError) },
      });
    }
    if (shareUrl) {
      list.push({ code: 'share-link', severity: 'info', key: 'ed.sharelink', params: { url: shareUrl } });
    }
    if (offline) list.push({ code: 'offline', severity: 'info', key: 'bn.offline' });
    return list;
  }, [conflict, saveState, saveError, shareUrl, offline, t]);

  const banners = useFlightBanners(extraBanners);

  const bannerActions = useMemo<Record<string, ReactNode>>(
    () => ({
      'sync-conflict': (
        <>
          <button type="button" className="ae-btn" onClick={takeServer}>
            {t('ed.conflict.take')}
          </button>
          <button type="button" className="ae-btn" onClick={keepMine}>
            {t('ed.conflict.keep')}
          </button>
        </>
      ),
      'save-failed': (
        <button type="button" className="ae-btn" onClick={() => void push()}>
          {t('ed.retry')}
        </button>
      ),
      'share-link': (
        <>
          <button
            type="button"
            className="ae-btn"
            onClick={() => void navigator.clipboard?.writeText(shareUrl ?? '')}
          >
            {t('ed.copy')}
          </button>
          <button type="button" className="ae-btn" onClick={() => setShareUrl(null)}>
            {t('ed.close')}
          </button>
        </>
      ),
    }),
    // takeServer/keepMine close over `conflict`, which is in the dependency list
    [t, conflict, shareUrl, push],
  );

  if (loadError) {
    return (
      <AppShell active="flights">
        <div className="ae-page">
          <h1>{t('ed.notavailable')}</h1>
          <p className="ae-banner warning">{api.apiMessage(t, loadError)}</p>
          <p>
            <Link to="/flights">{t('ed.toplans')}</Link>
          </p>
        </div>
      </AppShell>
    );
  }

  const rename = () => {
    const next = window.prompt(t('ed.title.ph'), title);
    if (next !== null) setTitle(next);
  };

  /* The panes come FIRST — that is what the tab this sheet opened from is
     about, and it is what the sheet's own doc comment and §4.2 both say. The
     document actions follow; the rail's screens come after the rule. */
  const overflow: MoreEntry[] = [
    ...TABS.filter(x => x.key !== 'route' && x.key !== 'navlog' && x.key !== 'weather').map(x => ({
      key: x.key,
      label: t(x.labelKey),
      Icon: x.Icon,
      onSelect: () => onTab(x.key),
    })),
    { key: 'pdf', label: t('ed.pdf'), Icon: IconExport, onSelect: () => void onPdf() },
    ...(planId ? [{ key: 'rename', label: t('flights.rename'), Icon: IconForm, onSelect: rename }] : []),
    {
      key: 'autocalc',
      label: t('ed.autocalc'),
      Icon: IconNavLog,
      checked: autocalc,
      onSelect: () => {
        setAutocalc(v => {
          store.setAutocalc(!v);
          return !v;
        });
      },
    },
    /* NOT `export`: the Export PANE's key is `export` too, and two entries
       with one React key is the duplicate-key warning the phone logged. */
    { key: 'file-save', label: t('ed.export'), Icon: IconExport, onSelect: onExport },
    { key: 'file-open', label: t('ed.import'), Icon: IconExport, onSelect: () => fileRef.current?.click() },
    { key: 'clear', label: t('ed.clear'), Icon: IconMore, danger: true, onSelect: onClear },
    ...(planId ? [{ key: 'share', label: t('ed.share'), Icon: IconExport, onSelect: () => void onShare() }] : []),
  ];

  const summaryActions = (
    <>
      <span className="ae-summary-state">{statusText[saveState]}</span>
      {narrow ? null : (
        <button type="button" className="ae-btn" disabled={pdfBusy} onClick={() => void onPdf()}>
          {pdfBusy ? t('ed.pdf.busy') : t('ed.pdf')}
        </button>
      )}
      <button
        type="button"
        className="ae-btn ae-btn-icon"
        aria-label={t('ed.more')}
        aria-expanded={more}
        onClick={() => setMore(v => !v)}
      >
        <IconMore />
      </button>
    </>
  );

  const top = (
    <>
      <SummaryStrip actions={summaryActions} />
      <Banners banners={banners} actions={bannerActions} onGoto={onTab} />
    </>
  );

  /* ---- phone: the tab bar switches what sits over the map ---- */
  const phoneTab = tab === 'navlog' || tab === 'weather' ? tab : tab === 'route' ? 'map' : 'more';
  const tabItems: TabBarItem[] = [
    { key: 'map', label: t('flight.tab.map'), Icon: IconMap, onSelect: () => onTab('route') },
    { key: 'navlog', label: t('insp.navlog'), Icon: IconNavLog, onSelect: () => onTab('navlog') },
    { key: 'weather', label: t('insp.weather'), Icon: IconWeather, onSelect: () => onTab('weather') },
    { key: 'more', label: t('nav.more'), Icon: IconMore, onSelect: () => setMore(true) },
  ];

  const paneCtx = {
    readOnly: false,
    aircraft,
    aircraftId,
    ...(session.user ? { onPickAircraft: setAircraftId } : {}),
  };

  const activeTab = TABS.find(x => x.key === tab) ?? TABS[0];

  const body = narrow ? (
    <div className="ae-workspace phone">
      <div className="ae-map">
        {/* the map keeps its own bottom sheet for the route; every other pane
            rides in the shell's sheet above it, so the canvas never unmounts */}
        <RouteMap panel={tab === 'route'} />
      </div>
      {tab === 'route' ? null : (
        <PaneSheet title={t(activeTab?.labelKey ?? '')} onClose={() => onTab('route')}>
          {activeTab?.render(paneCtx)}
        </PaneSheet>
      )}
    </div>
  ) : (
    <div
      className={
        'ae-workspace' +
        (collapsed ? ' collapsed' : '') +
        /* the NavLog is the one pane that is a sheet: it gets the inspector
           width its six flying columns need (shell.css, desktop only) */
        (tab === 'navlog' ? ' wideinspector' : '')
      }
    >
      <div className="ae-map">
        <RouteMap panel={false} />
      </div>
      <Inspector
        tab={tab}
        onTab={onTab}
        ctx={paneCtx}
        collapsed={collapsed}
        onToggleCollapse={() => {
          setCollapsed(v => {
            try {
              localStorage.setItem(LS_INSPECTOR, v ? '0' : '1');
            } catch {
              /* the choice simply does not survive the tab */
            }
            return !v;
          });
        }}
      />
    </div>
  );

  const header = narrow ? (
    <header className="ae-phonehead">
      <button
        type="button"
        className="ae-btn ae-btn-icon"
        aria-label={t('flight.back')}
        onClick={() => navigate('/flights')}
      >
        <IconBack />
      </button>
      <span className="ae-phonetitle">
        {[store.val('von'), store.val('nach')].filter(Boolean).join(' → ') || title || t('flights.noRoute')}
      </span>
    </header>
  ) : undefined;

  return (
    <ReadOnlyProvider value={false}>
      <WindsProvider>
        <TerrainProvider>
          <WxProvider>
            <AppShell
              /* Not "flights" for the signed-out local flight at `/`: the rail
                 would mark a screen current that redirects to /login, and
                 tapping it walks away from unsaved work. */
              {...(session.user || planId ? ({ active: 'flights' } as const) : {})}
              fill
              top={top}
              {...(header ? { header } : {})}
              {...(narrow ? { tabBar: <TabBar items={tabItems} active={phoneTab} /> } : {})}
            >
              {loaded ? body : <div className="ae-page">{t('loading')}</div>}
            </AppShell>
            {more ? <MoreSheet entries={overflow} onClose={() => setMore(false)} /> : null}
            <input
              ref={fileRef}
              type="file"
              hidden
              accept="application/json,.json"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) void onImportFile(f);
                e.target.value = '';
                setMore(false);
              }}
            />
          </WxProvider>
        </TerrainProvider>
      </WindsProvider>
    </ReadOnlyProvider>
  );
}
